import { FACET_ORDER, FACET_VALUE_LABELS } from './labels';
import type { FilterPatch, SearchQuery } from './search-state';

/**
 * The active filters, as one removable chip per selected VALUE.
 *
 * The sidebar already shows what is ticked, but only if you are looking at it: with eight
 * dimensions the list is taller than the viewport, so a filter set three scrolls down is invisible
 * while it is actively narrowing the results. That is the state where someone concludes the search
 * is broken. These chips keep every constraint in one line next to the count it produced.
 *
 * ONE CHIP PER VALUE, not per dimension — removing בינוי must not also drop תשתיות. Clearing a whole
 * dimension at once is what the sidebar's own `נקה (n)` is for.
 *
 * The search text gets no chip: the box is on screen, holds its own value and clears itself.
 */

interface Chip {
  key: string;
  label: string;
  remove: FilterPatch;
}

/** Every selected value, in sidebar order, so the chips and the sidebar read the same way. */
function chipsOf(query: SearchQuery): Chip[] {
  const chips: Chip[] = [];
  for (const dimension of FACET_ORDER) {
    const selected = query[dimension] ?? [];
    for (const value of selected) {
      chips.push({
        key: `${dimension}:${value}`,
        label: FACET_VALUE_LABELS[dimension][value] ?? value,
        // Rebuilt from the CURRENT selection rather than a stored index, so removing one chip
        // cannot shift what another one points at.
        remove: { [dimension]: without(selected, value) } as FilterPatch,
      });
    }
  }
  return chips;
}

/** Drop one value, collapsing an emptied dimension to `undefined` — the URL's one spelling for "no filter". */
function without(values: readonly string[], value: string): string[] | undefined {
  const next = values.filter((v) => v !== value);
  return next.length ? next : undefined;
}

export interface ActiveFiltersProps {
  query: SearchQuery;
  setFilter: (patch: FilterPatch) => void;
}

export function ActiveFilters({ query, setFilter }: ActiveFiltersProps) {
  const chips = chipsOf(query);
  if (chips.length === 0) return null;

  const clearAll = Object.fromEntries(FACET_ORDER.map((d) => [d, undefined])) as FilterPatch;

  return (
    <div className="tenders-chips" aria-label="מסננים פעילים">
      <span className="tenders-chips__lead">מסננים פעילים</span>
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          className="tenders-chip"
          aria-label={`הסרת הסינון ${chip.label}`}
          onClick={() => setFilter(chip.remove)}
        >
          <span>{chip.label}</span>
          <span className="tenders-chip__x" aria-hidden="true">
            ×
          </span>
        </button>
      ))}
      <button type="button" className="tenders-chips__clear" onClick={() => setFilter(clearAll)}>
        ניקוי הכל
      </button>
    </div>
  );
}
